/* eslint-disable react/require-default-props */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerFooter,
  Link,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss24Regular, ArrowRepeatAll20Regular, Warning20Regular } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { DrawerStackProvider, StackedDrawer } from '@bingads-webui-campaign-react/drawer-stack';

import { useTroubleshootDrawerStyles } from './use-troubleshoot-drawer-styles';
import { TroubleshootDetailDrawer, type TroubleshootOption } from './troubleshoot-detail-drawer';

export interface TroubleshootDrawerProps {
  open: boolean;
  onClose: () => void;
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
}

const DRAWER_WIDTH = '438px';

// @aiContributed-MAPFuse-2026-04-27
export const TroubleshootDrawer: React.FC<TroubleshootDrawerProps> = ({ open, onClose, logger }) => {
  const i18n = useContext(I18nContext);
  const styles = useTroubleshootDrawerStyles();
  const [detailOption, setDetailOption] = useState<TroubleshootOption | null>(null);

  const handleOptionClick = useCallback((option: TroubleshootOption) => {
    logger?.trace(`TroubleshootDrawer: Selected option=${option}`);
    setDetailOption(option);
  }, [logger]);

  const handleDetailClose = useCallback(() => {
    setDetailOption(null);
  }, []);

  const handleClose = useCallback(() => {
    logger?.trace('TroubleshootDrawer: Closed main drawer');
    setDetailOption(null);
    onClose();
  }, [onClose, logger]);

  const isDetailOpen = open && !!detailOption;

  const openDrawers = useMemo(() => {
    const drawers: string[] = [];
    if (open) drawers.push('troubleshoot-main');
    if (isDetailOpen) drawers.push('troubleshoot-detail');
    return drawers;
  }, [open, isDetailOpen]);

  return (
    <DrawerStackProvider drawerWidth={DRAWER_WIDTH} openDrawers={openDrawers}>
      <StackedDrawer
        className="troubleshoot-main-drawer"
        name="troubleshoot-main"
        onOpenChange={(_, { open: isOpen }) => {
          if (!isOpen) {
            handleClose();
          }
        }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={i18n.getString(_TL_('Close'))}
                icon={<Dismiss24Regular />}
                onClick={handleClose}
              />
            }
          >
            {i18n.getString(_TL_('Get help signing in to your account'))}
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody className={mergeClasses('troubleshoot-main-body', styles.body)}>
          <div>
            <Body1 className={styles.introText}>
              {i18n.getString(_TL_('First, what\'s happening?'))}
            </Body1>

            <div className={styles.optionCards}>
              <div
                className={mergeClasses('troubleshoot-option-page-loop', styles.optionCard)}
                role="button"
                tabIndex={0}
                onClick={() => handleOptionClick('pageLoop')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOptionClick('pageLoop'); } }}
              >
                <ArrowRepeatAll20Regular className={styles.optionIcon} />
                <Body1Strong className={styles.optionText}>
                  {i18n.getString(_TL_('The page won\'t load or it keeps refreshing'))}
                </Body1Strong>
              </div>

              <div
                className={mergeClasses('troubleshoot-option-error-message', styles.optionCard)}
                role="button"
                tabIndex={0}
                onClick={() => handleOptionClick('errorMessage')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOptionClick('errorMessage'); } }}
              >
                <Warning20Regular className={styles.optionIcon} />
                <Body1Strong className={styles.optionText}>
                  {i18n.getString(_TL_('I get an error explaining something is wrong or not working'))}
                </Body1Strong>
              </div>
            </div>
          </div>

          <div className={mergeClasses('troubleshoot-support-section', styles.supportSection)}>
            <Body1>
              <ReactTemplate
                template={i18n.getString(_TL_('Still need help? {{a}}Contact support{{/a}}'))}
                model={{
                  a: (text: string) => (
                    <Link
                      href="https://go.microsoft.com/fwlink/?LinkId=398371"
                      target="_blank"
                      rel="noopener noreferrer"
                      inline
                      onClick={() => logger?.trace('TroubleshootDrawer: Clicked Contact support link')}
                    >
                      {text}
                    </Link>
                  ),
                }}
              />
            </Body1>
            <Caption1 className={styles.supportCaption}>
              {i18n.getString(_TL_('For account security, we may ask you to verify your identity'))}
            </Caption1>
          </div>
        </DrawerBody>

        <DrawerFooter>
          <Button className="troubleshoot-close-button" appearance="primary" onClick={handleClose}>
            {i18n.getString(_TL_('Close'))}
          </Button>
        </DrawerFooter>
      </StackedDrawer>

      <TroubleshootDetailDrawer
        option={detailOption}
        onClose={handleDetailClose}
        logger={logger}
      />
    </DrawerStackProvider>
  );
};
